/**
 * AppPermissionsDetailDrawer: the full detail of one OAuth app, as a Carbon
 * side panel (components/carbon/SidePanel: a dialog on layer-01, a pinned
 * header, a scrolling body, a 60% scrim). The body is built from the
 * expanded-row parts (RowDetailSection, DiffList, ButtonSet) in one column:
 *   the app        risk tier by shape and word, publisher, flags, Entra link
 *   Guardian's read  the risk score, Guardian's summary and recommendation
 *   Review         the review decision and its workflow (approve,
 *                  investigate or flag, with a reason and a review period)
 *   Credentials    certificates, secrets and the next expiry
 *   Governance     how the app was granted and who it reaches
 *   Directory roles, Owners, Permissions (each list is one server-side page,
 *                  highest risk first; the headings carry the full counts)
 * Bad states are neutral (the Neutral Bad State Rule): words and the
 * high-contrast tag, with Coral only on a Critical severity glyph.
 *
 * The export name and props are unchanged. `AppPermissionsDetailContent` is
 * the body on its own, an additive export.
 */

import { useState, type ReactNode } from 'react'
import { Button, ButtonLink } from '@/components/carbon/Button'
import { Checkbox } from '@/components/carbon/Checkbox'
import { DiffList, type DiffRow } from '@/components/carbon/DiffList'
import { IconExternal } from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IuvoMark } from '@/components/carbon/IuvoMark'
import { ButtonSet, RowDetailSection } from '@/components/carbon/RowDetail'
import { Select } from '@/components/carbon/Select'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { severityFromApi } from '@/components/carbon/severity'
import { SidePanel } from '@/components/carbon/SidePanel'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Tag } from '@/components/carbon/Tag'
import { TextArea } from '@/components/carbon/TextArea'
import { Accordion } from '@/components/carbon/Accordion'
import { AppPermissionsRiskGauge } from './AppPermissionsRiskGauge'
import type { OAuthAppDetail, OAuthAppPermission, AcknowledgeOAuthAppRequest } from './appPermissionsTypes'

interface AppPermissionsDetailDrawerProps {
  app: OAuthAppDetail | null
  isOpen: boolean
  isLoading: boolean
  onClose: () => void
  /** Called when an acknowledgment is submitted; parent handles the API call and data refresh */
  onAcknowledge?: (appId: string, request: AcknowledgeOAuthAppRequest) => Promise<void>
}

/** Guardian's recommendation verbs, in words. */
const ACTION_WORD: Record<string, string> = {
  REVIEW: 'Review',
  REDUCE: 'Reduce scopes',
  REVOKE: 'Revoke',
  ACCEPT: 'Approve',
}

/** Roles considered critical severity when assigned to a service principal */
const CRITICAL_ROLES = [
  'Global Administrator',
  'Privileged Role Administrator',
  'Security Administrator',
  'Exchange Administrator',
  'SharePoint Administrator',
  'Intune Administrator',
  'Application Administrator',
  'Cloud Application Administrator',
]

/** Roles considered high severity */
const HIGH_ROLES = [
  'User Administrator',
  'Groups Administrator',
  'Authentication Administrator',
  'Conditional Access Administrator',
  'Compliance Administrator',
]

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null
  const diff = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function getDirectoryRoleSeverity(role: string): 'Critical' | 'High' | 'Medium' {
  if (CRITICAL_ROLES.some(r => role.includes(r))) return 'Critical'
  if (HIGH_ROLES.some(r => role.includes(r))) return 'High'
  return 'Medium'
}

/** Deep link into the Entra admin-centre Managed App blade for an enterprise
 *  application. `objectId` is the service-principal id; `appId` is the
 *  application (client) id. Both are required. `blade` chooses which tab the
 *  link lands on — Overview is the default; Owners is used by the no-owners
 *  notice below. */
function entraAppUrl(
  servicePrincipalId: string,
  appId: string,
  blade: 'Overview' | 'Owners' | 'Permissions' = 'Overview',
): string {
  return `https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/${blade}/objectId/${encodeURIComponent(
    servicePrincipalId,
  )}/appId/${encodeURIComponent(appId)}`
}

export function AppPermissionsDetailDrawer({ app, isOpen, isLoading, onClose, onAcknowledge }: AppPermissionsDetailDrawerProps) {
  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      size="lg"
      title={!isLoading && app ? app.displayName : 'App detail'}
      subtitle="Assessed by Guardian"
    >
      {isLoading ? (
        <DrawerSkeleton />
      ) : app ? (
        <AppPermissionsDetailContent key={app.id} app={app} onAcknowledge={onAcknowledge} />
      ) : (
        <p className="m-0 text-body text-text-secondary">
          This app&rsquo;s detail did not load. Close the panel and open the app again.
        </p>
      )}
    </SidePanel>
  )
}

/** The panel body: every section of one app's detail, stacked in one column. */
export function AppPermissionsDetailContent({
  app,
  onAcknowledge,
}: {
  app: OAuthAppDetail
  onAcknowledge?: (appId: string, request: AcknowledgeOAuthAppRequest) => Promise<void>
}) {
  const hasRead = app.aiRiskScore !== null || !!app.aiRiskSummary

  return (
    <div className="flex flex-col gap-8">
      <AppIdentity app={app} />

      {hasRead ? <GuardianRead app={app} /> : null}

      <AcknowledgmentSection app={app} onAcknowledge={onAcknowledge} />

      <CredentialHygieneSection app={app} />

      <RowDetailSection title="Governance">
        <DiffList
          rows={[
            { label: 'Consent type', value: app.consentType || '—' },
            { label: 'App created', value: formatDate(app.createdDateTime) },
            {
              label: 'Who it reaches',
              value: app.appRoleAssignmentRequired ? (
                <Tag>Assigned users only</Tag>
              ) : (
                <Tag kind="high-contrast">Everyone in the organization</Tag>
              ),
            },
          ]}
        />
      </RowDetailSection>

      {app.directoryRoles && app.directoryRoles.length > 0 ? (
        <RowDetailSection title="Directory roles">
          <ul className="m-0 list-none p-0">
            {app.directoryRoles.map(role => {
              const severity = getDirectoryRoleSeverity(role)
              return (
                <li
                  key={role}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-line-subtle py-2 first:border-t-0 first:pt-0"
                >
                  <span className="min-w-0 break-words font-semibold text-text-primary">{role}</span>
                  <SeverityGlyph level={severityFromApi(severity)} size={12}>
                    {severity}
                  </SeverityGlyph>
                </li>
              )
            })}
          </ul>
        </RowDetailSection>
      ) : null}

      <RowDetailSection title="Owners">
        {app.owners && app.owners.length > 0 ? (
          <ul className="m-0 list-none p-0">
            {app.owners.map(owner => (
              <li key={owner} className="break-words border-t border-line-subtle py-2 text-text-primary first:border-t-0 first:pt-0">
                {owner}
              </li>
            ))}
          </ul>
        ) : (
          <NoOwnersNotice servicePrincipalId={app.servicePrincipalId} appId={app.appId} />
        )}
      </RowDetailSection>

      <RowDetailSection title="Permissions">
        <Accordion
          className="-mx-4"
          headingLevel="h4"
          defaultOpen={['application', 'delegated']}
          items={[
            {
              id: 'application',
              title: 'Application permissions',
              meta: permissionTotal(app.applicationPermissionCount, app.applicationPermissions).toLocaleString(),
              content: (
                <PermissionList
                  permissions={app.applicationPermissions}
                  total={permissionTotal(app.applicationPermissionCount, app.applicationPermissions)}
                />
              ),
            },
            {
              id: 'delegated',
              title: 'Delegated permissions',
              meta: permissionTotal(app.delegatedPermissionCount, app.delegatedPermissions).toLocaleString(),
              content: (
                <PermissionList
                  permissions={app.delegatedPermissions}
                  total={permissionTotal(app.delegatedPermissionCount, app.delegatedPermissions)}
                />
              ),
            },
          ]}
        />
      </RowDetailSection>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   The app: tier, publisher, flags, Entra link
   ──────────────────────────────────────────────────────────────────── */

function AppIdentity({ app }: { app: OAuthAppDetail }) {
  const flags: ReactNode[] = []
  if (app.isFirstParty) flags.push(<Tag key="first">First party</Tag>)
  if (app.isMultiTenant) flags.push(<Tag key="multi">Multi-tenant</Tag>)
  if (!app.accountEnabled) flags.push(<Tag key="disabled" kind="high-contrast">Disabled</Tag>)
  if (app.isStale) flags.push(<Tag key="stale" kind="high-contrast">Stale</Tag>)

  return (
    <section aria-label="App summary" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <SeverityGlyph level={severityFromApi(app.overallRiskTier)}>{app.overallRiskTier} risk</SeverityGlyph>
        {app.isVerifiedPublisher ? (
          <Tag title="Microsoft has verified this publisher">Verified publisher</Tag>
        ) : !app.isFirstParty ? (
          <Tag kind="outline">Unverified publisher</Tag>
        ) : null}
      </div>
      <DiffList
        rows={[
          { label: 'Publisher', value: <span className="break-words text-text-primary">{app.publisherName || 'Unknown publisher'}</span> },
          { label: 'Application ID', value: <span className="break-all font-mono text-[13px] text-text-primary">{app.appId}</span> },
        ]}
      />
      {flags.length > 0 ? <div className="flex flex-wrap gap-1.5">{flags}</div> : null}
      <div>
        <ButtonLink href={entraAppUrl(app.servicePrincipalId, app.appId)} kind="tertiary" size="md" icon={<IconExternal />}>
          Open in Entra
        </ButtonLink>
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Guardian's read: risk score, summary, recommendation
   ──────────────────────────────────────────────────────────────────── */

function GuardianRead({ app }: { app: OAuthAppDetail }) {
  return (
    <section aria-labelledby="app-guardian-read" className="bg-layer-2 p-4 text-body text-text-secondary">
      <h3 id="app-guardian-read" className="m-0 mb-4 flex items-center gap-2 text-[14px] font-semibold leading-[1.29] text-text-primary">
        <IuvoMark size={14} />
        Guardian&rsquo;s read
      </h3>
      <div className="flex flex-col gap-4">
        <AppPermissionsRiskGauge score={app.aiRiskScore} tier={app.overallRiskTier} />
        {app.aiRiskSummary ? <p className="m-0 text-text-secondary">{app.aiRiskSummary}</p> : null}
        {app.aiRecommendation ? (
          <div>
            <p className="m-0 mb-2 flex flex-wrap items-center gap-2 font-semibold text-text-primary">
              Recommendation
              {app.aiRecommendationAction ? <Tag>{ACTION_WORD[app.aiRecommendationAction] ?? app.aiRecommendationAction}</Tag> : null}
            </p>
            <p className="m-0 text-text-secondary">{app.aiRecommendation}</p>
          </div>
        ) : null}
        {app.aiEnrichedAt ? (
          <p className="m-0 text-helper tabular-nums text-text-helper">Analyzed {formatDate(app.aiEnrichedAt)}</p>
        ) : null}
      </div>
    </section>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Owners
   ──────────────────────────────────────────────────────────────────── */

function NoOwnersNotice({
  servicePrincipalId,
  appId,
}: {
  servicePrincipalId: string
  appId: string
}) {
  return (
    <div className="flex flex-col gap-3">
      <InlineNotification kind="info" title="No owner is assigned.">
        Assign one in Microsoft Entra so someone is accountable for renewals, consent reviews and incident response.
      </InlineNotification>
      <div>
        <ButtonLink href={entraAppUrl(servicePrincipalId, appId, 'Owners')} kind="tertiary" size="md" icon={<IconExternal />}>
          Assign an owner in Entra
        </ButtonLink>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Credential Hygiene
   ──────────────────────────────────────────────────────────────────── */

const AUTH_METHOD_WORD: Record<string, string> = {
  Certificate: 'Certificate',
  Secret: 'Secret',
  Both: 'Certificate and secret',
  FederatedIdentity: 'Federated identity',
  None: 'None',
}

function CredentialHygieneSection({ app }: { app: OAuthAppDetail }) {
  const credentialType = app.credentialType || 'None'
  const expiryDays = daysUntil(app.earliestCredentialExpiry)
  const isExternal = app.isExternalApp && credentialType === 'None'

  if (isExternal) {
    return (
      <RowDetailSection title="Credentials">
        <p className="m-0">
          <b>Managed by the publisher.</b> This multi-tenant app is registered in another organization, so its
          certificates and secrets live in the publisher&rsquo;s tenant and are not visible from this one.
        </p>
      </RowDetailSection>
    )
  }

  const status: ReactNode =
    credentialType === 'None' ? (
      <span className="text-text-helper">No credentials configured</span>
    ) : app.hasExpiredCredentials ? (
      <Tag kind="high-contrast">Expired</Tag>
    ) : app.hasExpiringCredentials || (expiryDays !== null && expiryDays <= 30) ? (
      <Tag kind="high-contrast">Expiring</Tag>
    ) : (
      <Tag>All current</Tag>
    )

  const rows: DiffRow[] = [
    { label: 'Auth method', value: <span className="text-text-primary">{AUTH_METHOD_WORD[credentialType] ?? credentialType}</span> },
    { label: 'Certificates', value: <span className="tabular-nums text-text-primary">{app.certificateCount} active</span> },
    { label: 'Secrets', value: <span className="tabular-nums text-text-primary">{app.secretCount}</span> },
  ]
  if (app.federatedCredentialCount > 0) {
    rows.push({ label: 'Federated', value: <span className="tabular-nums text-text-primary">{app.federatedCredentialCount}</span> })
  }
  if (app.earliestCredentialExpiry) {
    rows.push({
      label: 'Next expiry',
      value: (
        <span className="tabular-nums text-text-primary">
          {formatDate(app.earliestCredentialExpiry)}
          {expiryDays !== null ? (
            <span className="text-text-helper">
              {' '}
              ({expiryDays < 0 ? `${Math.abs(expiryDays)} days ago` : `in ${expiryDays} day${expiryDays === 1 ? '' : 's'}`})
            </span>
          ) : null}
        </span>
      ),
    })
  }
  rows.push({ label: 'Status', value: status })

  const showNotice = credentialType !== 'None' && (app.hasExpiredCredentials || app.hasExpiringCredentials)
  const noticeTitle = app.hasExpiredCredentials ? 'A credential has expired.' : 'A credential expires soon.'
  const noticeBody = app.hasExpiredCredentials
    ? 'Rotate the secret or certificate so the app can authenticate again.'
    : expiryDays !== null && expiryDays >= 0
      ? `The next one lapses in ${expiryDays} day${expiryDays === 1 ? '' : 's'}. Rotate it before then.`
      : 'One is inside the 30-day expiry window. Rotate it before it lapses.'

  return (
    <RowDetailSection title="Credentials">
      {showNotice ? (
        <InlineNotification kind="info" title={noticeTitle} className="mb-4">
          {noticeBody}
        </InlineNotification>
      ) : null}
      <DiffList rows={rows} />
    </RowDetailSection>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Acknowledgment Section
   ──────────────────────────────────────────────────────────────────── */

const VERIFICATION_CHECKLIST = [
  'Verify app identity and publisher',
  'Confirm business purpose',
  'Review permissions vs stated purpose',
  'Check scoping (org-wide vs assigned users)',
  'Verify credential hygiene',
  'Assess multi-tenant risk',
  'Review sign-in activity',
]

const STATUS_OPTIONS = [
  { value: 'Approved', label: 'Approved' },
  { value: 'Investigating', label: 'Investigating' },
  { value: 'Flagged', label: 'Flagged' },
]

const PERIOD_OPTIONS = [
  { value: '3', label: '3 months' },
  { value: '6', label: '6 months' },
  { value: '12', label: '12 months' },
  { value: 'none', label: 'No expiry' },
]

function AcknowledgmentSection({
  app,
  onAcknowledge,
}: {
  app: OAuthAppDetail
  onAcknowledge?: (appId: string, request: AcknowledgeOAuthAppRequest) => Promise<void>
}) {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formStatus, setFormStatus] = useState<string>(app.acknowledgmentStatus || 'Approved')
  const [reason, setReason] = useState(app.acknowledgmentReason || '')
  const [expiryMonths, setExpiryMonths] = useState<string>('6')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [checkedItems, setCheckedItems] = useState<boolean[]>(new Array(VERIFICATION_CHECKLIST.length).fill(false))
  const [showChecklist, setShowChecklist] = useState(false)

  const isAcknowledged = !!app.acknowledgmentStatus
  const isExpired = app.acknowledgmentExpiresAt ? new Date(app.acknowledgmentExpiresAt) < new Date() : false
  const ackDaysUntil = daysUntil(app.acknowledgmentExpiresAt)
  const isReviewDueSoon = !isExpired && ackDaysUntil !== null && ackDaysUntil <= 14
  const isHighRisk = app.overallRiskTier === 'Critical' || app.overallRiskTier === 'High'
  const checkedCount = checkedItems.filter(Boolean).length

  const handleSubmit = async () => {
    if (!reason.trim() || !onAcknowledge) return
    setIsSubmitting(true)
    try {
      await onAcknowledge(app.id, {
        status: formStatus,
        reason: reason.trim(),
        expiresInMonths: expiryMonths === 'none' ? undefined : parseInt(expiryMonths, 10),
      })
      setIsFormOpen(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleCheckItem = (index: number) => {
    setCheckedItems(prev => {
      const next = [...prev]
      next[index] = !next[index]
      return next
    })
  }

  return (
    <RowDetailSection title="Review">
      {isAcknowledged && !isFormOpen ? (
        /* The current review decision */
        <div className="flex flex-col gap-4">
          {isExpired ? (
            <InlineNotification kind="info" title="The review has lapsed.">
              Re-review this app before extending trust.
            </InlineNotification>
          ) : null}
          {isReviewDueSoon ? (
            <InlineNotification kind="info" title="A re-review is due soon.">
              {ackDaysUntil === 0
                ? 'It is due today. Confirm this app still belongs.'
                : `It is due in ${ackDaysUntil} day${ackDaysUntil === 1 ? '' : 's'}. Confirm this app still belongs.`}
            </InlineNotification>
          ) : null}
          <DiffList
            rows={[
              {
                label: 'Decision',
                value: <Tag kind={app.acknowledgmentStatus === 'Flagged' ? 'high-contrast' : 'default'}>{app.acknowledgmentStatus}</Tag>,
              },
              { label: 'Reviewed by', value: <span className="break-all text-text-primary">{app.acknowledgedBy || '—'}</span> },
              { label: 'Reviewed on', value: <span className="tabular-nums text-text-primary">{formatDate(app.acknowledgedAt)}</span> },
              {
                label: 'Expires',
                value: (
                  <span className="tabular-nums text-text-primary">
                    {app.acknowledgmentExpiresAt ? formatDate(app.acknowledgmentExpiresAt) : 'No expiry'}
                    {isExpired ? <span className="text-text-helper"> (lapsed)</span> : null}
                  </span>
                ),
              },
            ]}
          />
          {app.acknowledgmentReason ? (
            <p className="m-0">
              <b>Reason.</b> {app.acknowledgmentReason}
            </p>
          ) : null}
          <div>
            <Button
              kind="tertiary"
              size="md"
              onClick={() => {
                setFormStatus(app.acknowledgmentStatus || 'Approved')
                setReason(app.acknowledgmentReason || '')
                setIsFormOpen(true)
              }}
            >
              Update the review
            </Button>
          </div>
        </div>
      ) : !isFormOpen ? (
        <div className="flex flex-col gap-4">
          <p className="m-0">Nobody has reviewed this app yet. Record a decision to keep an audit trail.</p>
          <div>
            <Button kind="primary" onClick={() => setIsFormOpen(true)}>
              Review this app
            </Button>
          </div>
        </div>
      ) : null}

      {/* The review form */}
      {isFormOpen ? (
        <form
          className="flex flex-col gap-6"
          onSubmit={e => {
            e.preventDefault()
            void handleSubmit()
          }}
        >
          {isHighRisk ? (
            <div>
              <Button
                kind="ghost"
                size="sm"
                aria-expanded={showChecklist}
                aria-controls="app-review-checklist"
                onClick={() => setShowChecklist(prev => !prev)}
                className="-ml-3"
              >
                {showChecklist ? 'Hide the verification checklist' : 'Show the verification checklist'} ({checkedCount} of{' '}
                {VERIFICATION_CHECKLIST.length} done)
              </Button>
              {showChecklist ? (
                <ul id="app-review-checklist" className="m-0 mt-2 grid list-none gap-2 p-0">
                  {VERIFICATION_CHECKLIST.map((item, i) => (
                    <li key={item}>
                      <Checkbox label={item} checked={checkedItems[i]} onChange={() => toggleCheckItem(i)} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          <Select label="Decision" value={formStatus} onChange={e => setFormStatus(e.target.value)} options={STATUS_OPTIONS} />

          <TextArea
            label="Reason (required)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="The business justification, or your investigation notes"
            rows={3}
            required
          />

          <Select label="Review again in" value={expiryMonths} onChange={e => setExpiryMonths(e.target.value)} options={PERIOD_OPTIONS} />

          <ButtonSet>
            <Button type="button" kind="secondary" onClick={() => setIsFormOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" kind="primary" loading={isSubmitting} disabled={!reason.trim() || !onAcknowledge}>
              {isSubmitting ? 'Saving the review' : 'Save the review'}
            </Button>
          </ButtonSet>
        </form>
      ) : null}
    </RowDetailSection>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Permissions
   ──────────────────────────────────────────────────────────────────── */

/**
 * The full count of one permission list. The detail endpoint pages each list
 * server-side (H-10) and reports the total separately; an older payload without
 * a usable total falls back to the rows it carried.
 */
function permissionTotal(count: number | undefined, permissions: OAuthAppPermission[]): number {
  return typeof count === 'number' && count >= permissions.length ? count : permissions.length
}

function PermissionList({ permissions, total }: { permissions: OAuthAppPermission[]; total: number }) {
  if (permissions.length === 0) {
    return <p className="m-0 text-text-helper">No permissions of this type.</p>
  }
  return (
    <>
      <PermissionRows permissions={permissions} />
      {total > permissions.length ? (
        <p className="m-0 mt-3 text-helper text-text-helper">
          Showing the {permissions.length.toLocaleString()} highest-risk of {total.toLocaleString()}. Open the app in
          Entra for the full list.
        </p>
      ) : null}
    </>
  )
}

function PermissionRows({ permissions }: { permissions: OAuthAppPermission[] }) {
  return (
    <ul className="m-0 list-none p-0">
      {permissions.map(perm => (
        <li key={perm.id} className="border-t border-line-subtle py-3 first:border-t-0 first:pt-0">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <code className="min-w-0 break-all font-mono text-[13px] font-semibold text-text-primary">{perm.permissionName}</code>
            <SeverityGlyph level={severityFromApi(perm.riskTier)} size={12}>
              {perm.riskTier}
            </SeverityGlyph>
          </div>
          <p className="m-0 mt-0.5 text-helper text-text-helper">{perm.resourceDisplayName}</p>
          {perm.permissionDescription ? (
            <p className="m-0 mt-1 text-helper text-text-secondary">{perm.permissionDescription}</p>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/* ────────────────────────────────────────────────────────────────────
   Loading skeleton
   ──────────────────────────────────────────────────────────────────── */

function DrawerSkeleton() {
  return (
    <div className="flex flex-col gap-8">
      <Skeleton lines={2} label="Loading the app detail" />
      <div className="bg-layer-2 p-4">
        <Skeleton lines={4} />
      </div>
      <Skeleton lines={3} />
    </div>
  )
}
